import { Feature, Geometry } from "geojson";
import mapboxgl, { CirclePaint, LinePaint, MapboxGeoJSONFeature, PointLike } from "mapbox-gl";

import { booleanIntersects, lineString } from "fond/turf";

const EMPTY_FC = { type: "FeatureCollection" as const, features: [] };
const SEGMENT_QUERY_BUFFER_PX = 5;

/**
 * Adds a temporary preview source + line layer to the map, used by the snap_add / snap_delete
 * draw modes to show which features the user is about to commit while a drag is in progress.
 */
export const ensurePreviewLayer = (map: mapboxgl.Map, sourceId: string, layerId: string, paint: LinePaint): void => {
  if (!map.getSource(sourceId)) {
    map.addSource(sourceId, { type: "geojson", data: EMPTY_FC });
  }
  if (!map.getLayer(layerId)) {
    map.addLayer({
      id: layerId,
      type: "line",
      source: sourceId,
      layout: { "line-cap": "round", "line-join": "round" },
      paint,
    });
  }
};

export const setPreviewFeatures = (map: mapboxgl.Map, sourceId: string, features: Array<Feature<Geometry>>): void => {
  const source = map.getSource(sourceId) as mapboxgl.GeoJSONSource | undefined;
  if (!source) return;
  source.setData({ type: "FeatureCollection", features });
};

export const removePreviewLayer = (map: mapboxgl.Map, sourceId: string, layerId: string): void => {
  if (map.getLayer(layerId)) map.removeLayer(layerId);
  if (map.getSource(sourceId)) map.removeSource(sourceId);
};

/**
 * Adds a temporary preview source + circle layer to the map, used to show the snapped
 * graph node in shortest_path_feature mode. Features set via setPreviewFeatures should be
 * Point geometries.
 */
export const ensurePreviewCircleLayer = (map: mapboxgl.Map, sourceId: string, layerId: string, paint: CirclePaint): void => {
  if (!map.getSource(sourceId)) {
    map.addSource(sourceId, { type: "geojson", data: EMPTY_FC });
  }
  if (!map.getLayer(layerId)) {
    map.addLayer({ id: layerId, type: "circle", source: sourceId, paint });
  }
};

/**
 * Returns every rendered feature (matching `featureFilter`) that intersects the screen-space
 * segment from `from` to `to`. Querying along the whole segment — rather than just at the endpoint —
 * means a fast mouse drag doesn't skip over thin features between onDrag events.
 *
 * When `from` is null or the segment is degenerate (< 1px), falls back to a point query at `to`.
 */
export const findHitsAlongSegment = (
  map: mapboxgl.Map,
  from: [number, number] | null,
  to: [number, number],
  featureFilter: (feature: MapboxGeoJSONFeature) => boolean
): MapboxGeoJSONFeature[] => {
  if (!from) {
    return map.queryRenderedFeatures(to).filter(featureFilter);
  }
  const [x1, y1] = from;
  const [x2, y2] = to;
  const dx = x2 - x1;
  const dy = y2 - y1;
  if (Math.abs(dx) < 1 && Math.abs(dy) < 1) {
    return map.queryRenderedFeatures(to).filter(featureFilter);
  }

  const bbox: [PointLike, PointLike] = [
    [Math.min(x1, x2) - SEGMENT_QUERY_BUFFER_PX, Math.min(y1, y2) - SEGMENT_QUERY_BUFFER_PX],
    [Math.max(x1, x2) + SEGMENT_QUERY_BUFFER_PX, Math.max(y1, y2) + SEGMENT_QUERY_BUFFER_PX],
  ];
  const candidates = map.queryRenderedFeatures(bbox).filter(featureFilter);
  if (candidates.length === 0) return [];

  const fromLngLat = map.unproject(from);
  const toLngLat = map.unproject(to);
  const segment = lineString([
    [fromLngLat.lng, fromLngLat.lat],
    [toLngLat.lng, toLngLat.lat],
  ]);

  return candidates.filter((f) => booleanIntersects(f, segment));
};
